// HomePage.tsx
import React, { useEffect, useState } from "react";
import styles from "./HomePage.module.css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { normalizeLanguageCode } from "../supportedLanguages";
import globalFeatureContent from "../globalFeatureContent.json";
import storeReviewContent from "../storeReviewContent.json";
import {
  getMicrosoftStoreUrl,
  trackMicrosoftStoreClick,
} from "../utils/microsoftStore";

type StoreReviewLabels = (typeof storeReviewContent.labels)["en"];
type GlobalFeatureContent = (typeof globalFeatureContent)["en"];
type StoreReviewTranslation = [string, string];
type StoreReview = {
  id: string;
  originalLanguage: string;
  originalTitle: string;
  originalBody: string;
  author: string | null;
  date?: string;
};

type DisplayReview = StoreReview & {
  title: string;
  body: string;
  isTranslation: boolean;
};

const storeReviewLabelsByLanguage = storeReviewContent.labels as Record<
  string,
  StoreReviewLabels
>;
const globalFeatureContentByLanguage = globalFeatureContent as Record<
  string,
  GlobalFeatureContent
>;
const storeReviewTranslationsByLanguage =
  storeReviewContent.translations as unknown as Record<
    string,
    StoreReviewTranslation[]
  >;
const storeReviews = storeReviewContent.reviews as StoreReview[];

const getDisplayReviews = (language: string): DisplayReview[] => {
  const translations =
    storeReviewTranslationsByLanguage[language] ||
    storeReviewTranslationsByLanguage.en;

  return storeReviews.map((review, index) => {
    const translation = translations[index] ||
      storeReviewTranslationsByLanguage.en[index];
    const isTranslation = language !== review.originalLanguage;

    return {
      ...review,
      title: isTranslation ? translation[0] : review.originalTitle,
      body: isTranslation ? translation[1] : review.originalBody,
      isTranslation,
    };
  });
};

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const normalizedLanguage = normalizeLanguageCode(currentLang);
  const storeReviewLabels =
    storeReviewLabelsByLanguage[normalizedLanguage] ||
    storeReviewLabelsByLanguage.en;
  const localizedGlobalFeatures =
    globalFeatureContentByLanguage[normalizedLanguage] ||
    globalFeatureContentByLanguage.en;
  const displayReviews = getDisplayReviews(normalizedLanguage);
  const slidingReviews = [...displayReviews, ...displayReviews];
  const useCaseLanguage = ["en", "ko", "ja"].includes(normalizedLanguage)
    ? normalizedLanguage
    : "en";

  const downloadUrls = {
    hero: getMicrosoftStoreUrl("home_hero"),
    workflow: getMicrosoftStoreUrl("home_workflow"),
    footer: getMicrosoftStoreUrl("home_footer"),
  };
  const representativeVideos = {
    shorts: {
      youtubeId: "LKPPmixLoL8",
      title: t("home_page.results.shorts.label", {
        defaultValue: "Shorts Video",
      }),
    },
    longform: {
      youtubeId: "EeFlgVAlalQ",
      title: t("home_page.results.longform.label", {
        defaultValue: "Long-form Video",
      }),
    },
  };

  // 🔹 3개의 타이틀/서브타이틀 셋업
  const heroMessages = [
    {
      title: t("home_page.hero.messages.0.title"),
      subtitle1: t("home_page.hero.messages.0.subtitle1"),
      subtitle2: t("home_page.hero.messages.0.subtitle2"),
    },
    {
      title: t("home_page.hero.messages.1.title"),
      subtitle1: t("home_page.hero.messages.1.subtitle1"),
      subtitle2: t("home_page.hero.messages.1.subtitle2"),
    },
    {
      title: t("home_page.hero.messages.2.title"),
      subtitle1: t("home_page.hero.messages.2.subtitle1"),
      subtitle2: t("home_page.hero.messages.2.subtitle2"),
    },
  ];

  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [reduceHeroMotion, setReduceHeroMotion] = useState(false);
  const heroFrames = [
    {
      src: "/assets/videos/hero-workflow/guided-workflow.mp4",
      label: localizedGlobalFeatures.guidedWorkflowTitle,
      durationMs: 5000,
    },
    {
      src: "/assets/videos/hero-workflow/image-motion.mp4",
      label: t("how_to_use.steps.image_motion.title"),
      durationMs: 5000,
    },
    {
      src: "/assets/videos/hero-workflow/subtitle-effects.mp4",
      label: localizedGlobalFeatures.subtitleEffectsTitle,
      durationMs: 5000,
    },
    {
      src: "/assets/videos/hero-workflow/final-action.mp4",
      label: t("home_page.results.eyebrow", {
        defaultValue: "Actual Generated Video",
      }),
      durationMs: 10000,
    },
  ];
  const currentHeroDuration = heroFrames[currentHeroIndex].durationMs;

  useEffect(() => {
    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => {
      setReduceHeroMotion(motionPreference.matches);
    };

    updateMotionPreference();
    motionPreference.addEventListener("change", updateMotionPreference);
    return () => {
      motionPreference.removeEventListener("change", updateMotionPreference);
    };
  }, []);

  useEffect(() => {
    if (reduceHeroMotion) return;

    const timer = window.setTimeout(() => {
      setCurrentHeroIndex((previousIndex) =>
        (previousIndex + 1) % heroFrames.length,
      );
    }, currentHeroDuration);

    return () => window.clearTimeout(timer);
  }, [
    currentHeroDuration,
    currentHeroIndex,
    heroFrames.length,
    reduceHeroMotion,
  ]);

  const activeMessage = heroMessages[0];

  return (
    <div className={styles.homePageContainer}>
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.heroTopRow}>
            <span className={styles.heroBadge}>AUTOVID · AI VIDEO STUDIO</span>
          </div>

          <div className={styles.heroTextWrapper}>
            <div className={styles.heroTextBlock}>
              <h1 className={styles.heroTitle}>{activeMessage.title}</h1>
              <p className={styles.heroSubtitle}>{activeMessage.subtitle1}</p>
              <p className={styles.heroSubtitle}>{activeMessage.subtitle2}</p>

              <div className={styles.heroActions}>
                <a
                  href={downloadUrls.hero}
                  className={styles.primaryButton}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackMicrosoftStoreClick("home_hero")}
                >
                  {t("home_page.hero.primary_cta")}
                </a>
                <a href="#home-results" className={styles.ghostButton}>
                  {t("home_page.results.cta", {
                    defaultValue: "View actual generated videos",
                  })}
                </a>
              </div>

              <p className={styles.heroNote}>{t("home_page.hero.note")}</p>
            </div>
          </div>
        </div>

        <div className={styles.heroVisual}>
          {heroFrames.map((frame, index) => (
            <figure
              className={`${styles.heroFrame} ${
                index === currentHeroIndex ? styles.heroFrameActive : ""
              }`}
              style={
                {
                  "--hero-frame-duration": `${frame.durationMs}ms`,
                } as React.CSSProperties
              }
              aria-hidden={index !== currentHeroIndex}
              key={frame.src}
            >
              <div className={styles.heroFrameGlow} aria-hidden="true" />
              <div className={styles.heroFrameScratches} aria-hidden="true" />
              <div className={styles.heroFrameMediaWrap}>
                <div className={styles.heroFrameChrome} aria-hidden="true">
                  <span className={styles.heroFrameDots} />
                  <span>AUTOVID PREVIEW</span>
                </div>
                <video
                  key={index === currentHeroIndex ? "active" : "inactive"}
                  src={frame.src}
                  autoPlay={
                    index === currentHeroIndex && !reduceHeroMotion
                  }
                  muted
                  playsInline
                  preload="auto"
                  aria-label={
                    index === currentHeroIndex ? frame.label : undefined
                  }
                />
                <div className={styles.heroFrameReflection} aria-hidden="true" />
                {index < heroFrames.length - 1 && (
                  <div className={styles.heroFrameGlint} aria-hidden="true" />
                )}
                <div className={styles.heroFrameProgress} aria-hidden="true">
                  <span />
                </div>
              </div>
              <figcaption>
                <span>0{index + 1}</span>
                {frame.label}
              </figcaption>
            </figure>
          ))}

          <div className={styles.heroFrameControls} aria-label="AUTOVID workflow">
            {heroFrames.map((frame, index) => (
              <button
                type="button"
                className={`${styles.heroFrameButton} ${
                  index === currentHeroIndex ? styles.heroFrameButtonActive : ""
                }`}
                onClick={() => setCurrentHeroIndex(index)}
                aria-label={frame.label}
                aria-current={index === currentHeroIndex ? "true" : undefined}
                key={frame.src}
              >
                0{index + 1}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.resultSection} id="home-results">
        <div className={styles.resultHeader}>
          <span className={styles.resultEyebrow}>
            {t("home_page.results.eyebrow", {
              defaultValue: "Actual Generated Videos",
            })}
          </span>
          <h2 className={styles.resultTitle}>
            {t("home_page.results.title", {
              defaultValue:
                "Automatically generated results without manual editing",
            })}
          </h2>
          <p className={styles.resultLanguageNote}>
            {t("home_page.results.language_note", {
              defaultValue:
                "The output language can be changed in the program.",
            })}
          </p>
        </div>

        <div className={styles.resultGrid}>
          <article className={styles.resultCard}>
            <div className={styles.resultVideoFrame}>
              <iframe
                src={`https://www.youtube.com/embed/${representativeVideos.shorts.youtubeId}`}
                title={representativeVideos.shorts.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <span className={styles.resultType}>
              {representativeVideos.shorts.title}
            </span>
          </article>

          <article className={styles.resultCard}>
            <div className={styles.resultVideoFrame}>
              <iframe
                src={`https://www.youtube.com/embed/${representativeVideos.longform.youtubeId}`}
                title={representativeVideos.longform.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <span className={styles.resultType}>
              {representativeVideos.longform.title}
            </span>
          </article>

        </div>

        <div className={styles.resultCtaRow}>
          <Link
            to={`/${currentLang}/sample-videos`}
            className={styles.resultCta}
          >
            {t("home_page.results.cta", {
              defaultValue: "View all result videos",
            })}
          </Link>
        </div>
      </section>

      <section className={styles.useCaseSection} aria-labelledby="home-use-cases-title">
        <h2 className={styles.sectionTitle} id="home-use-cases-title">
          {t("home_page.features.title")}
        </h2>
        <p className={styles.sectionLead}>{t("home_page.features.lead")}</p>
        <div className={styles.useCaseGrid}>
          <Link to={`/${useCaseLanguage}/ai-shorts-maker`} className={styles.useCaseCard}>
            <span>AI SHORTS</span>
            <h3>{localizedGlobalFeatures.guidedWorkflowTitle}</h3>
            <p>{localizedGlobalFeatures.guidedWorkflowText}</p>
          </Link>
          <Link to={`/${useCaseLanguage}/marketing-video-maker`} className={styles.useCaseCard}>
            <span>MARKETING VIDEO</span>
            <h3>{localizedGlobalFeatures.marketingTitle}</h3>
            <p>{localizedGlobalFeatures.marketingText}</p>
          </Link>
        </div>
      </section>

      {/* 2. 문제 섹션 */}
      <section className={styles.problemSection}>
        <h2 className={styles.sectionTitle}>{t("home_page.problem.title")}</h2>
        <div className={styles.problemGrid}>
          <div className={styles.problemItem}>
            <h3>{t("home_page.problem.items.time.title")}</h3>
            <p>{t("home_page.problem.items.time.text")}</p>
          </div>
          <div className={styles.problemItem}>
            <h3>{t("home_page.problem.items.quality.title")}</h3>
            <p>{t("home_page.problem.items.quality.text")}</p>
          </div>
          <div className={styles.problemItem}>
            <h3>{t("home_page.problem.items.algorithm.title")}</h3>
            <p>{t("home_page.problem.items.algorithm.text")}</p>
          </div>
          <div className={styles.problemItem}>
            <h3>{t("home_page.problem.items.philosophy.title")}</h3>
            <p>{t("home_page.problem.items.philosophy.text")}</p>
          </div>
        </div>
      </section>

      {/* 3. 가이드 섹션 */}
      <section className={styles.guideSection}>
        <h2 className={styles.sectionTitle}>{t("home_page.guide.title")}</h2>
        <p className={styles.sectionLead}>{t("home_page.guide.lead")}</p>
        <ul className={styles.guideList}>
          <li>{t("home_page.guide.points.0")}</li>
          <li>{t("home_page.guide.points.1")}</li>
          <li>{t("home_page.guide.points.2")}</li>
        </ul>
      </section>

      {/* 4. 단계별 제작 안내 섹션 */}
      <section className={styles.planSection}>
        <h2 className={styles.sectionTitle}>
          {localizedGlobalFeatures.guidedWorkflowTitle}
        </h2>
        <p className={styles.sectionLead}>
          {localizedGlobalFeatures.guidedWorkflowText}
        </p>

        <div className={styles.planGrid}>
          <div className={styles.planItem}>
            <span className={styles.planIndex}>01</span>
            <h3>{localizedGlobalFeatures.guidedWorkflowPoint1}</h3>
            <p>{t("home_page.plan.steps.1.text")}</p>
          </div>
          <div className={styles.planItem}>
            <span className={styles.planIndex}>02</span>
            <h3>{localizedGlobalFeatures.guidedWorkflowPoint2}</h3>
            <p>{t("home_page.plan.steps.2.text")}</p>
          </div>
          <div className={styles.planItem}>
            <span className={styles.planIndex}>03</span>
            <h3>{localizedGlobalFeatures.guidedWorkflowPoint3}</h3>
            <p>{t("home_page.plan.steps.3.text")}</p>
          </div>
        </div>

        <div className={styles.centerCta}>
          <a
            href={downloadUrls.workflow}
            className={styles.primaryButton}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackMicrosoftStoreClick("home_workflow")}
          >
            {t("home_page.plan.cta")}
          </a>
        </div>
      </section>

      {/* 5. 기능 섹션 + 이미지 (여기에 아까 말한 7개 기능/이미지 매핑) */}
      <section className={styles.featuresSection}>
        <h2 className={styles.sectionTitle}>{t("home_page.features.title")}</h2>
        <p className={styles.sectionLead}>{t("home_page.features.lead")}</p>

        <div className={styles.featureGrid}>
          {/* 1. 쇼츠·롱폼 템플릿 */}
          <article className={styles.featureCard}>
            <img
              src="/assets/images/features/template.png"
              alt="Templates"
              className={styles.featureImage}
            />
            <h3>{t("home_page.features.templates.title")}</h3>
            <p>{t("home_page.features.templates.text")}</p>
          </article>

          {/* 2. 썸네일 자동 생성 */}
          <article className={styles.featureCard}>
            <img
              src="/assets/images/features/thumbnail_gen.png"
              alt="Thumbnail"
              className={styles.featureImage}
            />
            <h3>{t("home_page.features.thumbnail.title")}</h3>
            <p>{t("home_page.features.thumbnail.text")}</p>
          </article>

          {/* 3. 원클릭 유튜브 업로드 */}
          <article className={styles.featureCard}>
            <img
              src="/assets/images/features/youtube_upload.png"
              alt="YouTube Upload"
              className={styles.featureImage}
            />
            <h3>{t("home_page.features.upload.title")}</h3>
            <p>{t("home_page.features.upload.text")}</p>
          </article>

          {/* 4. 장면용 시각 자료 옵션 */}
          <article className={styles.featureCard}>
            <img
              src="/assets/images/features/ai_image_model.png"
              alt="AI model options"
              className={styles.featureImage}
            />
            <h3>{t("home_page.features.ai_models.title")}</h3>
            <p>{t("home_page.features.ai_models.text")}</p>
          </article>

          {/* 5. 음성/자막 타이밍 */}
          <article className={styles.featureCard}>
            <img
              src="/assets/images/features/auto_subtitle.png"
              alt="Sync"
              className={styles.featureImage}
            />
            <h3>{t("home_page.features.sync.title")}</h3>
            <p>{t("home_page.features.sync.text")}</p>
          </article>

          {/* 6. 유튜브 최적화 대본 생성 */}
          <article className={styles.featureCard}>
            <img
              src="/assets/images/features/seo.png"
              alt="YouTube Script"
              className={styles.featureImage}
            />
            <h3>{t("home_page.features.script.title")}</h3>
            <p>{t("home_page.features.script.text")}</p>
          </article>
        </div>

        <div className={styles.globalFeatureGrid}>
          <article className={styles.globalFeatureCard}>
            <h3>{localizedGlobalFeatures.marketingTitle}</h3>
            <p>{localizedGlobalFeatures.marketingText}</p>
          </article>
          <article className={styles.globalFeatureCard}>
            <h3>{localizedGlobalFeatures.globalTitle}</h3>
            <p>{localizedGlobalFeatures.globalText}</p>
          </article>
        </div>

        <div className={styles.centerCta}>
          <Link to={`/${currentLang}/features`} className={styles.ghostButton}>
            {t("home_page.features.more")}
          </Link>
        </div>
      </section>

      {/* 6. Microsoft Store 사용자 리뷰 */}
      <section className={styles.testimonialSection}>
        <div className={styles.testimonialHeading}>
          <div>
            <h2 className={styles.sectionTitle}>{storeReviewLabels.title}</h2>
            <div className={styles.ratingSummary}>
              <span className={styles.ratingStars} aria-hidden="true">
                ★★★★★
              </span>
              <strong>{storeReviewContent.rating} / 5</strong>
              <span>· {storeReviewLabels.ratingCount}</span>
            </div>
          </div>
          <time
            className={styles.reviewVerifiedDate}
            dateTime={storeReviewContent.verifiedDate}
          >
            {storeReviewLabels.verified}
          </time>
        </div>

        <div className={styles.testimonialViewport}>
          <div className={styles.testimonialTrack}>
            {slidingReviews.map((review, index) => {
              const isDuplicate = index >= displayReviews.length;
              const sourceLanguage = review.originalLanguage.toUpperCase();
              const targetLanguage = normalizedLanguage.toUpperCase();

              return (
                <article
                  className={styles.testimonialCard}
                  key={`${review.id}-${isDuplicate ? "copy" : "original"}`}
                  aria-hidden={isDuplicate || undefined}
                >
                  <div className={styles.testimonialCardHeader}>
                    <span className={styles.ratingStars} aria-hidden="true">
                      ★★★★★
                    </span>
                    <span className={styles.reviewLanguage}>
                      {review.isTranslation
                        ? `${sourceLanguage} → ${targetLanguage}`
                        : sourceLanguage}
                    </span>
                  </div>
                  <h3 className={styles.testimonialTitle}>{review.title}</h3>
                  <blockquote
                    className={styles.testimonialText}
                    lang={review.isTranslation
                      ? normalizedLanguage
                      : review.originalLanguage}
                  >
                    “{review.body}”
                  </blockquote>
                  {review.isTranslation && (
                    <details className={styles.originalReview}>
                      <summary tabIndex={isDuplicate ? -1 : undefined}>
                        {sourceLanguage}
                      </summary>
                      <strong lang={review.originalLanguage}>
                        {review.originalTitle}
                      </strong>
                      <p lang={review.originalLanguage}>
                        {review.originalBody}
                      </p>
                    </details>
                  )}
                  <footer className={styles.testimonialFooter}>
                    {(review.author || review.date) && (
                      <p className={styles.testimonialAuthor}>
                        {review.author}
                        {review.author && review.date && " · "}
                        {review.date && (
                          <time dateTime={review.date}>{review.date}</time>
                        )}
                      </p>
                    )}
                    <a
                      href={storeReviewContent.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.reviewSourceLink}
                      tabIndex={isDuplicate ? -1 : undefined}
                    >
                      {storeReviewLabels.sourceLink}
                    </a>
                  </footer>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. 마지막 CTA 섹션 */}
      <section className={styles.finalCtaSection}>
        <h2 className={styles.sectionTitle}>{t("home_page.final.title")}</h2>
        <p className={styles.sectionLead}>{t("home_page.final.lead1")}</p>
        <p className={styles.sectionLead}>{t("home_page.final.lead2")}</p>

        <div className={styles.finalCtaButtons}>
          <a
            href={downloadUrls.footer}
            className={styles.primaryButton}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackMicrosoftStoreClick("home_footer")}
          >
            {t("home_page.final.primary_cta")}
          </a>
          <Link to={`/${currentLang}/features`} className={styles.ghostButton}>
            {t("home_page.final.secondary_cta2")}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
